import type { AxiosResponse } from 'axios'
import type { components } from 'schema.ts'

import type { IFetchApiProps } from '~/portal/api'
import { FETCH_API_METHODS, fetchApi } from '~/portal/api'
import { getFilenameFromHeaders } from '~/portal/api/api.helpers.ts'
import { STATE_STORE_KEYS } from '~/portal/types'

export interface IFetchApiProductList {
	token: IFetchApiProps['token']
	projectId?: number
	params?: {
		page?: string | number
		page_size?: string | number
		search?: string | number
		manufacturer_id?: string | number
		type_id?: string | number
		type?: string
		return_all?: boolean
	}
}

export interface IFetchApiProductAttributeList {
	token: IFetchApiProps['token']
	params?: {
		page?: string | number
		type?: 'CONTROLLER' | 'PROTECTOR' | 'PRODUCT'
	}
}

export interface IFetchApiProductTypeList {
	token: IFetchApiProps['token']
	params?: {
		page?: string | number
		return_all?: boolean
		type?: string
	}
}

export interface IFetchApiGetManufacturerList {
	token: IFetchApiProps['token']
	params?: {
		page?: number
		return_all?: boolean
		type?: string
	}
}

export interface IFetchApiUpdateDBProduct {
	id: string | number
	token: IFetchApiProps['token']
	payload: components['schemas']['UpdateProduct']
}

export interface IFetchApiCreateProduct {
	token: IFetchApiProps['token']
	projectId?: number
	payload: components['schemas']['CreateProduct']
}

export interface IFetchApiFindProduct {
	token: IFetchApiProps['token']
	projectId: number
	payload: any
}

export interface IFetchApiExportProduct {
	token: IFetchApiProps['token']
	id: number
}

export interface IFetchApiDeleteProduct {
	token: IFetchApiProps['token']
	projectId: number
	id: number
	params: {
		type: string
	}
}

export interface IFetchApiDeleteDbProduct {
	token: IFetchApiProps['token']
	id: number
}

export interface IFetchApiUpdateAttributeValue {
	token: IFetchApiProps['token']
	projectId: number
	id: number
	payload: {
		attributes: {
			id: number
			value: string
		}[]
		amount?: number
	}
}

export interface IFetchApiProductFilters {
	token: IFetchApiProps['token']
	params: {
		manufacturer_id: string | number
		product_type: string
	}
}

export interface IFetchApiProductImport {
	token: IFetchApiProps['token']
	params?: {
		type?: string
		name?: string
	}
	payload: {
		name: string
		base64: string
	}
}

export interface IFetchApiUpdateProjectProduct {
	token: IFetchApiProps['token']
	projectId: number
	id: number
	payload: components['schemas']['UpdateProductProject']
}

export const fetchApiProductList = async (payload: IFetchApiProductList) => {
	const url = payload.projectId
		? `/project/${payload.projectId}/product/list`
		: '/product/list'
	return await fetchApi({
		url,
		token: payload.token,
		method: FETCH_API_METHODS.Get,
		pendingKey: STATE_STORE_KEYS.ProductList,
		params: payload.params
	})
}

export const fetchApiProductAttributeList = async (
	payload: IFetchApiProductAttributeList
) => {
	return await fetchApi({
		url: 'product/attribute/list?return_all=true',
		token: payload.token,
		method: FETCH_API_METHODS.Get,
		pendingKey: STATE_STORE_KEYS.ProductAttributeList,
		params: payload.params
	})
}

export const fetchApiProductTypeList = async (
	payload: IFetchApiProductTypeList
) => {
	return await fetchApi({
		url: '/product/type/list',
		token: payload.token,
		method: FETCH_API_METHODS.Get,
		pendingKey: STATE_STORE_KEYS.ProductTypeList,
		params: {
			...payload.params,
			return_all: true
		}
	})
}

export const fetchApiGetManufacturerList = async (
	payload: IFetchApiGetManufacturerList
) => {
	const response = await fetchApi({
		token: payload.token,
		method: FETCH_API_METHODS.Get,
		pendingKey: STATE_STORE_KEYS.ManufacturerList,
		url: '/product/manufactuer/list',
		params: {
			...payload.params,
			return_all: true
		}
	})

	if (response?.data) {
		return response.data
	}
}

export const fetchApiCreateProduct = async ({
	payload,
	projectId,
	token
}: IFetchApiCreateProduct) => {
	const url = projectId ? `/project/${projectId}/product` : '/product'
	await fetchApi({
		url,
		method: FETCH_API_METHODS.Post,
		pendingKey: STATE_STORE_KEYS.ProductCreate,
		token,
		payload
	})
}

export const fetchApiUpdateDBProduct = async ({
	payload,
	id,
	token
}: IFetchApiUpdateDBProduct) => {
	await fetchApi({
		url: `/product/${id}/update`,
		method: FETCH_API_METHODS.Put,
		token,
		pendingKey: STATE_STORE_KEYS.ProductDBUpdate,
		payload
	})
}

export const fetchApiFindProduct = ({
	payload,
	projectId,
	token
}: IFetchApiFindProduct) => {
	return fetchApi({
		url: `/project/${projectId}/product/find`,
		method: FETCH_API_METHODS.Post,
		token,
		pendingKey: STATE_STORE_KEYS.ProductFind,
		payload
	})
}

export interface IFetchApiExportProductTemplate {
	token: string
}

export const fetchApiExportProduct = async ({
	token,
	id
}: IFetchApiExportProduct): Promise<{
	blob: Blob
	filename: string | null
}> => {
	const url = `/product/export/${id}`
	const resp = (await fetchApi({
		url,
		method: FETCH_API_METHODS.Get,
		token,
		pendingKey: STATE_STORE_KEYS.ProductExport,
		responseType: 'blob'
	})) as AxiosResponse<Blob>

	const filename = getFilenameFromHeaders(
		resp.headers,
		url,
		`product-${id}.xlsx`
	)
	return { blob: resp.data, filename }
}

export const fetchApiExportProductTemplate = async ({
	token
}: IFetchApiExportProductTemplate): Promise<{
	blob: Blob
	filename: string | null
}> => {
	const url = '/product/export/template'
	const resp = (await fetchApi({
		url,
		method: FETCH_API_METHODS.Get,
		token,
		pendingKey: STATE_STORE_KEYS.ProductExportAll,
		responseType: 'blob'
	})) as AxiosResponse<Blob>

	const filename = getFilenameFromHeaders(
		resp.headers,
		url,
		'products-template.xlsx'
	)
	return { blob: resp.data, filename }
}

export const fetchApiDeleteDBProduct = async ({
	token,
	id
}: IFetchApiDeleteDbProduct) => {
	return fetchApi({
		url: `/product/${id}/delete`,
		method: FETCH_API_METHODS.Delete,
		token,
		pendingKey: STATE_STORE_KEYS.DbProductDelete
	})
}

export const fetchApiProductFilters = async ({
	token,
	params
}: IFetchApiProductFilters) => {
	return fetchApi({
		url: '/product/filters',
		method: FETCH_API_METHODS.Get,
		token,
		params,
		pendingKey: STATE_STORE_KEYS.ProductFilters
	})
}

export const fetchApiDeleteProduct = ({
	token,
	projectId,
	id,
	params
}: IFetchApiDeleteProduct) => {
	return fetchApi({
		url: `/project/${projectId}/product/${id}/delete`,
		method: FETCH_API_METHODS.Delete,
		token,
		pendingKey: STATE_STORE_KEYS.ProductDelete,
		params
	})
}

export const fetchApiUpdateAttributeValue = ({
	token,
	projectId,
	id,
	payload
}: IFetchApiUpdateAttributeValue) => {
	return fetchApi({
		url: `/project/${projectId}/product/${id}/update`,
		method: FETCH_API_METHODS.Put,
		token,
		pendingKey: STATE_STORE_KEYS.ProductDBUpdate,
		payload
	})
}

export const fetchApiProductImport = (payload: IFetchApiProductImport) => {
	return fetchApi({
		url: '/product/import',
		method: FETCH_API_METHODS.Post,
		token: payload.token,
		pendingKey: STATE_STORE_KEYS.ProductImport,
		payload: payload.payload,
		params: payload.params
	})
}

export const fetchApiUpdateProjectProduct = (
	payload: IFetchApiUpdateProjectProduct
) => {
	return fetchApi({
		url: `/project/${payload.projectId}/product/${payload.id}/update`,
		method: FETCH_API_METHODS.Put,
		token: payload.token,
		pendingKey: STATE_STORE_KEYS.ProductUpdate,
		payload: payload.payload
	})
}
